import { useEffect, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { Inbox } from 'lucide-react'
import { toast } from 'sonner'
import { useUserRole } from '@/hooks/useUserRole'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { platformApi, type SubmissionList } from '../api'
import { StatusBadge } from '../components/badges'

const STATUSES = ['draft', 'in_review', 'returned', 'approved', 'refused']

/** "My submissions" for everyone; managers can switch to all submissions. */
export default function SubmissionsListPage() {
    const { isRiskManager } = useUserRole()
    const [scope, setScope] = useState<'mine' | 'all'>('mine')
    const [status, setStatus] = useState('all')
    const [page, setPage] = useState(0)
    const [data, setData] = useState<SubmissionList | null>(null)
    const navigate = useNavigate()

    useEffect(() => {
        setData(null)
        platformApi
            .listSubmissions({ scope, status: status === 'all' ? '' : status, page, pageSize: 20 })
            .then(setData)
            .catch((e) => { toast.error(e.message); setData({ items: [], total: 0, page: 0, pageSize: 20 }) })
    }, [scope, status, page])

    const open = (id: string, st: string) =>
        navigate(st === 'draft' ? { to: '/submissions/$id/edit', params: { id } } : { to: '/submissions/$id', params: { id } })

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <h1 className="text-2xl font-semibold tracking-tight">{scope === 'mine' ? 'My requests' : 'All requests'}</h1>
                <div className="flex flex-wrap items-center gap-2">
                    {isRiskManager && (
                        <Tabs value={scope} onValueChange={(v) => { setScope(v as 'mine' | 'all'); setPage(0) }}>
                            <TabsList><TabsTrigger value="mine">Mine</TabsTrigger><TabsTrigger value="all">Everyone</TabsTrigger></TabsList>
                        </Tabs>
                    )}
                    <Select value={status} onValueChange={(v) => { setStatus(v); setPage(0) }}>
                        <SelectTrigger className="w-40" aria-label="Filter by status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {data === null ? (
                <div className="flex flex-col gap-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>
            ) : data.items.length === 0 ? (
                <Empty>
                    <EmptyHeader>
                        <EmptyMedia variant="icon"><Inbox /></EmptyMedia>
                        <EmptyTitle>Nothing here yet</EmptyTitle>
                        <EmptyDescription>Submissions you start will show up here.</EmptyDescription>
                    </EmptyHeader>
                    <Button asChild><Link to="/forms">Start a request</Link></Button>
                </Empty>
            ) : (
                <>
                    <div className="rounded-lg border bg-card">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Number</TableHead><TableHead>Form</TableHead><TableHead>Title</TableHead>
                                    {scope === 'all' && <TableHead>Submitter</TableHead>}
                                    <TableHead>Status</TableHead><TableHead>Updated</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.items.map((s) => (
                                    <TableRow key={s.id} className="cursor-pointer" onClick={() => open(s.id, s.status)}>
                                        <TableCell className="font-mono text-xs">{s.number}</TableCell>
                                        <TableCell className="font-medium">{s.form_name}</TableCell>
                                        <TableCell className="max-w-64 truncate text-muted-foreground">{s.title || '—'}</TableCell>
                                        {scope === 'all' && <TableCell>{s.submitter_name}</TableCell>}
                                        <TableCell><StatusBadge status={s.status} /></TableCell>
                                        <TableCell className="text-muted-foreground">{new Date(s.updated_at).toLocaleDateString()}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{data.total} total</span>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
                            <Button variant="outline" size="sm" disabled={(page + 1) * data.pageSize >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
